import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Building2, CalendarDays, Copy, ExternalLink, Layers, Plus, UserCheck, Users, UserSquare2, type LucideIcon } from "lucide-react";

import { ClassDetails } from "../components/classes/ClassDetails";
import { ClassForm, CopyYearForm, StreamsForm } from "../components/classes/ClassForms";
import { ClassesPanel, LevelsPanel, TeachersPanel } from "../components/classes/ClassPanels";
import { occupancy } from "../components/classes/classSetupLogic";
import { Notice, deleteErrorText, type NoticeState } from "../components/register/RecordParts";
import { usePermissions } from "../hooks/usePermissions";
import { academicSetupService } from "../services/academicSetupService";
import { studentService, type BulkClassCreateInput, type Class, type ClassCopyYearInput } from "../services/studentService";
import { apiErrorMessage } from "../utils/apiError";

type Tab = "classes" | "levels" | "teachers";
type Dialog = { kind: "create" } | { kind: "copy" } | { kind: "edit"; row: Class } | null;

/**
 * Classes and streams for an academic year: each level's streams, how full
 * they are, and who leads each one. The year is chosen at the top (the
 * current one by default) and every tab shows that year.
 */
export default function ClassStreamsSetup() {
  const { hasCapability } = usePermissions();
  const canWrite = hasCapability("access.students.class_write");
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("tab") === "levels" ? "levels" : params.get("tab") === "teachers" ? "teachers" : "classes";
  const [notice, setNotice] = useState<NoticeState>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [viewing, setViewing] = useState<number | null>(null);

  const years = useQuery({ queryKey: ["academic-years"], queryFn: academicSetupService.getYears });
  const yearList = [...(years.data ?? [])].sort((a, b) => b.start_date.localeCompare(a.start_date));
  const yearId = Number(params.get("year")) || yearList.find((item) => item.is_current)?.id || yearList[0]?.id;
  const year = yearList.find((item) => item.id === yearId);
  const yearName = year?.name ?? "this year";

  const classes = useQuery({
    queryKey: ["class-streams", "year", yearId],
    queryFn: () => studentService.getClasses({ ordering: "level", academic_year: yearId }),
    enabled: Boolean(yearId),
  });
  const teachers = useQuery({ queryKey: ["class-teacher-candidates"], queryFn: studentService.getClassTeacherCandidates, enabled: canWrite });
  const rows = classes.data?.results;
  const list = rows ?? [];

  const go = (next: { tab?: Tab; view?: string; level?: string; year?: number }) => {
    const query: Record<string, string> = {};
    const nextYear = next.year ?? yearId;
    if (params.get("year") || next.year) query.year = String(nextYear);
    if (next.tab && next.tab !== "classes") query.tab = next.tab;
    if (next.view) query.view = next.view;
    if (next.level) query.level = next.level;
    setParams(query, { replace: true });
  };

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["class-streams"] });
    await queryClient.invalidateQueries({ queryKey: ["classes"] });
  };
  const create = useMutation({
    mutationFn: (data: BulkClassCreateInput) => studentService.createClasses(data),
    onSuccess: async (result, data) => {
      await refresh();
      setDialog(null);
      setNotice({ tone: "success", text: `${data.level}: ${result.created_count} ${result.created_count === 1 ? "class" : "classes"} created (${result.classes.map((row) => row.name).join(", ")}).` });
      if (data.academic_year !== yearId) go({ year: data.academic_year });
    },
  });
  const copy = useMutation({
    mutationFn: (data: ClassCopyYearInput) => studentService.copyClassesFromYear(data),
    onSuccess: async (result, data) => {
      await refresh();
      setDialog(null);
      const skipped = result.skipped.length ? ` ${result.skipped.length} already there and left as they were: ${result.skipped.join(", ")}.` : "";
      setNotice({ tone: "success", text: `${result.created_count} ${result.created_count === 1 ? "class" : "classes"} copied.${skipped}` });
      go({ year: data.to_year });
    },
  });
  const update = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Record<string, unknown> }) => studentService.updateClass(id, data),
    onSuccess: async (saved, { data }) => {
      await refresh();
      setDialog(null);
      setNotice({ tone: "success", text: "class_teacher" in data && Object.keys(data).length === 1 ? `${saved.class_teacher_name} is now class teacher of ${saved.name}.` : `${saved.name} saved.` });
    },
    onError: (error) => {
      // A form shows the refusal under its fields; a one-click assignment says it here.
      if (!dialog) setNotice({ tone: "error", text: apiErrorMessage(error, "The class could not be saved.") });
    },
  });
  const remove = useMutation({
    mutationFn: (row: Class) => studentService.deleteClass(row.id),
    onSuccess: async (_, row) => {
      setViewing(null);
      await refresh();
      setNotice({ tone: "success", text: `${row.name} deleted.` });
    },
    onError: (error) => setNotice({ tone: "error", text: deleteErrorText(error) }),
  });

  const learners = list.reduce((sum, row) => sum + row.current_student_count, 0);
  const capacity = list.reduce((sum, row) => sum + row.capacity, 0);
  const free = list.reduce((sum, row) => sum + Math.max(0, row.available_spots), 0);
  const noTeacher = list.filter((row) => !row.class_teacher).length;
  const full = list.filter((row) => ["full", "over"].includes(occupancy(row.current_student_count, row.capacity).fill)).length;
  const over = list.filter((row) => row.current_student_count > row.capacity).length;
  const levelCount = new Set(list.map((row) => row.level)).size;
  const dash = (value: number | string) => (classes.isLoading ? "–" : String(value));
  const tiles: { label: string; value: string; note: string; go: () => void; icon: LucideIcon; alert?: boolean }[] = [
    { label: "Classes", value: dash(list.length), note: `${levelCount} ${levelCount === 1 ? "level" : "levels"}`, go: () => go({ tab: "levels" }), icon: Building2 },
    { label: "Learners in class", value: dash(learners.toLocaleString()), note: capacity ? `${Math.round((learners / capacity) * 100)}% of ${capacity.toLocaleString()} places` : "No places set up", go: () => go({}), icon: Users },
    { label: "Places free", value: dash(free.toLocaleString()), note: full ? `${full} ${full === 1 ? "class is" : "classes are"} full${over ? `, ${over} over` : ""}` : "No class is full", go: () => go({ view: full ? "full" : "space" }), icon: Layers, alert: over > 0 },
    { label: "No class teacher", value: dash(noTeacher), note: noTeacher ? "Assign one to each" : "Every class has one", go: () => go({ view: "no_teacher" }), icon: UserCheck, alert: noTeacher > 0 },
  ];
  const tabs: { id: Tab; label: string; icon: LucideIcon; badge?: number }[] = [
    { id: "classes", label: "Classes", icon: Building2 },
    { id: "levels", label: "Levels", icon: Layers },
    ...(canWrite ? [{ id: "teachers" as Tab, label: "Class teachers", icon: UserSquare2, badge: noTeacher }] : []),
  ];
  const opened = viewing ? list.find((row) => row.id === viewing) : undefined;
  const view = params.get("view") ?? undefined;
  const level = params.get("level") ?? undefined;
  const addActions = canWrite && (
    <div className="flex flex-wrap gap-2">
      <button type="button" className="btn btn-outline h-9 gap-1.5 text-sm" onClick={() => { copy.reset(); setDialog({ kind: "copy" }); }} disabled={yearList.length < 2}>
        <Copy className="h-4 w-4" /> Copy from another year
      </button>
      <button type="button" className="btn btn-primary h-9 gap-1.5 text-sm" onClick={() => { create.reset(); setDialog({ kind: "create" }); }} disabled={!yearList.length}>
        <Plus className="h-4 w-4" /> Add level and streams
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Classes & Streams</h1>
          <p className="mt-1 text-sm text-gray-500">Each level's streams for the year: how many places they have, how full they are, their rooms and who leads each one.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="filter-field w-full sm:w-auto sm:min-w-48">
            Academic year
            <select value={yearId ?? ""} onChange={(event) => go({ tab, year: Number(event.target.value) })} className="input-compact" disabled={!yearList.length}>
              {!yearList.length && <option value="">{years.isLoading ? "Loading…" : "No academic years"}</option>}
              {yearList.map((item) => <option key={item.id} value={item.id}>{item.name}{item.is_current ? " (current)" : ""}</option>)}
            </select>
          </label>
          <Link to="/academics" className="btn btn-outline h-9 gap-1.5 text-sm"><CalendarDays className="h-4 w-4" /> Years and terms <ExternalLink className="h-3.5 w-3.5" /></Link>
        </div>
      </div>

      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      {years.isSuccess && !yearList.length && (
        <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> Classes belong to an academic year. Set up the year in Academics first.
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((tile) => (
          <button
            key={tile.label}
            type="button"
            onClick={tile.go}
            className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tile.alert ? "bg-amber-50 text-amber-700" : "bg-primary-50 text-primary-700"}`}>
              <tile.icon className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-medium text-gray-500">{tile.label}</span>
              <span className={`block truncate text-xl font-semibold tabular-nums ${tile.alert ? "text-amber-800" : "text-gray-900"}`}>{tile.value}</span>
              <span className="block truncate text-xs text-gray-500">{tile.note}</span>
            </span>
          </button>
        ))}
      </div>

      <nav role="tablist" aria-label="Class setup sections" className="-mb-px flex gap-x-6 overflow-x-auto border-b border-gray-200">
        {tabs.map((item) => {
          const active = tab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => go({ tab: item.id })}
              className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-2.5 text-sm font-medium ${active ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"}`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
              {item.badge ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs tabular-nums text-amber-800">{item.badge}</span> : null}
            </button>
          );
        })}
      </nav>

      {tab === "classes" && (
        <ClassesPanel
          rows={rows}
          yearName={yearName}
          canWrite={canWrite}
          isLoading={classes.isLoading || years.isLoading}
          isError={classes.isError}
          onRetry={() => classes.refetch()}
          onOpen={(row) => setViewing(row.id)}
          actions={addActions}
          initialState={view || level ? { ...(view && { view }), ...(level && { category: level }) } : undefined}
        />
      )}
      {tab === "levels" && (
        <LevelsPanel rows={rows} yearName={yearName} isLoading={classes.isLoading || years.isLoading} isError={classes.isError} onRetry={() => classes.refetch()} onOpen={(next) => go({ level: next })} />
      )}
      {tab === "teachers" && canWrite && (
        <TeachersPanel
          teachers={teachers.data}
          classes={list}
          yearName={yearName}
          canWrite={canWrite}
          busy={update.isPending}
          isLoading={teachers.isLoading || classes.isLoading}
          isError={teachers.isError || classes.isError}
          onRetry={() => { teachers.refetch(); classes.refetch(); }}
          onOpenClass={(row) => setViewing(row.id)}
          onAssign={(row, teacher) => update.mutate({ id: row.id, data: { class_teacher: teacher.id } })}
        />
      )}

      {opened && !dialog && (
        <ClassDetails
          record={opened}
          canWrite={canWrite}
          busy={remove.isPending}
          onClose={() => setViewing(null)}
          onEdit={() => { update.reset(); setDialog({ kind: "edit", row: opened }); }}
          onDelete={() => { if (window.confirm(`Delete ${opened.name}? This cannot be undone.`)) remove.mutate(opened); }}
        />
      )}
      {dialog?.kind === "create" && (
        <StreamsForm
          years={yearList}
          yearId={yearId}
          classes={list}
          teachers={teachers.data ?? []}
          teachersLoading={teachers.isLoading}
          busy={create.isPending}
          error={create.isError ? apiErrorMessage(create.error, "The classes could not be created.") : undefined}
          onSave={(data) => create.mutate(data)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "edit" && (
        <ClassForm
          record={dialog.row}
          years={yearList}
          classes={list}
          teachers={teachers.data ?? []}
          teachersLoading={teachers.isLoading}
          busy={update.isPending}
          error={update.isError ? apiErrorMessage(update.error, "The class could not be saved.") : undefined}
          onSave={(data) => update.mutate({ id: dialog.row.id, data })}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "copy" && (
        <CopyYearForm
          years={yearList}
          toYear={yearId}
          busy={copy.isPending}
          error={copy.isError ? apiErrorMessage(copy.error, "The classes could not be copied.") : undefined}
          onSave={(data) => copy.mutate(data)}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}
